import { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { SeasonEditInput, SeasonInput, SeasonPhaseInput } from '@shared/schemas';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useCreatePhase,
  useDeletePhase,
  useUpdatePhase,
  useCreateSeason,
  useUpdateSeason,
  usePhases,
  useSeasons,
  useTeams,
} from '@/app/queries';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { formatDateRange } from '@/lib/format';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { Sheet, SheetNumber, SheetRow } from '@/ui/Sheet';
import { Chip } from '@/ui/Chip';

/** A season defaults to the northern-hemisphere football year around today. */
function defaultSeasonDates(): { startsOn: string; endsOn: string; label: string } {
  const now = new Date();
  const year = now.getUTCMonth() >= 6 ? now.getUTCFullYear() : now.getUTCFullYear() - 1;
  return {
    startsOn: `${year}-07-01`,
    endsOn: `${year + 1}-05-31`,
    label: `${year}/${year + 1}`,
  };
}

export function Seasons({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const seasons = useSeasons();
  const create = useCreateSeason();
  const { busy, formError, fieldError, submit } = useAuthForm();

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<SeasonInput>(() => ({
    teamId: '',
    status: 'planning',
    ...defaultSeasonDates(),
  }));

  const teamList = teams.data ?? [];

  // Prefill the team once it is known; a single-team coach should never have to
  // choose from a list of one.
  useEffect(() => {
    if (!form.teamId && teamList[0]) {
      setForm((f) => ({ ...f, teamId: teamList[0]!.id }));
    }
  }, [teamList, form.teamId]);

  const set = <K extends keyof SeasonInput>(k: K) => (value: SeasonInput[K]) =>
    setForm((f) => ({ ...f, [k]: value }));

  const list = seasons.data ?? [];

  if (teams.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (teamList.length === 0) {
    return (
      <>
        <PageHeader
        title={t('seasons.title')}
        lead={t('seasons.lead')}
        count={list.length || undefined}
        help={localePath(locale, '/guide/club')}
      />
        <Panel>
          <EmptyState title={t('teams.empty')} body={t('dashboard.noTeamBody')} />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('seasons.title')}
        lead={t('seasons.lead')}
        help={localePath(locale, '/guide/club')}
        action={
          <Button variant="primary" onClick={() => setOpen(true)}>
            {t('seasons.create')}
          </Button>
        }
      />

      {open ? (
        <div className="mb-5">
          <Panel title={t('seasons.create')}>
            <form
              noValidate
              className="grid gap-x-5 gap-y-2 p-4 sm:grid-cols-2"
              onSubmit={async (e) => {
                e.preventDefault();
                const ok = await submit(async () => {
                  await create.mutateAsync(form);
                });
                if (ok) setOpen(false);
              }}
            >
              {formError ? (
                <div className="sm:col-span-2">
                  <Notice tone="risk">{formError}</Notice>
                </div>
              ) : null}

              <Field label={t('seasons.label')} hint={t('seasons.labelHint')} error={fieldError('label')}>
                {(p) => (
                  <TextInput
                    {...p}
                    value={form.label}
                    onChange={(e) => set('label')(e.target.value)}
                    required
                    autoFocus
                  />
                )}
              </Field>

              <Field label={t('seasons.team')} error={fieldError('teamId')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={form.teamId}
                    onChange={(e) => set('teamId')(e.target.value)}
                    required
                  >
                    {teamList.map((tm) => (
                      <option key={tm.id} value={tm.id}>
                        {tm.name}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>

              <Field label={t('seasons.startsOn')} error={fieldError('startsOn')}>
                {(p) => (
                  <TextInput
                    {...p}
                    type="date"
                    value={form.startsOn}
                    onChange={(e) => set('startsOn')(e.target.value)}
                    required
                  />
                )}
              </Field>

              <Field label={t('seasons.endsOn')} error={fieldError('endsOn')}>
                {(p) => (
                  <TextInput
                    {...p}
                    type="date"
                    value={form.endsOn}
                    onChange={(e) => set('endsOn')(e.target.value)}
                    required
                  />
                )}
              </Field>

              <Field label={t('seasons.status')} error={fieldError('status')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={form.status}
                    onChange={(e) => set('status')(e.target.value as SeasonInput['status'])}
                  >
                    <option value="planning">{t('seasons.status_planning')}</option>
                    <option value="active">{t('seasons.status_active')}</option>
                    <option value="archived">{t('seasons.status_archived')}</option>
                  </SelectInput>
                )}
              </Field>

              <div className="flex flex-wrap gap-2 sm:col-span-2">
                <Button type="submit" variant="primary" busy={busy}>
                  {t('seasons.save')}
                </Button>
                <Button type="button" variant="quiet" onClick={() => setOpen(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </Panel>
        </div>
      ) : null}

      <Panel>
        {seasons.isLoading ? (
          <p className="flex items-center gap-2 px-4 py-8 text-ink-muted">
            <Spinner /> {t('common.loading')}
          </p>
        ) : list.length === 0 ? (
          <EmptyState
            title={t('seasons.empty')}
            body={t('seasons.emptyBody')}
            action={
              <Button variant="primary" onClick={() => setOpen(true)}>
                {t('seasons.create')}
              </Button>
            }
          />
        ) : (
          <Sheet>
            {list.map((s) => {
              const team = teamList.find((tm) => tm.id === s.teamId);
              return (
                <li key={s.id}>
                  <SheetRow onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{s.label}</span>
                      <span className="tabular text-meta text-ink-faint">
                        {formatDateRange(s.startsOn, s.endsOn, locale)}
                      </span>
                    </span>
                    {team ? <Chip muted>{team.name}</Chip> : null}
                    <Chip>{t(`seasons.status_${s.status}`)}</Chip>
                    <button
                      type="button"
                      className="inline-flex min-h-11 min-w-11 items-center justify-center text-meta text-ink-faint underline hover:text-ink"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditing(editing === s.id ? null : s.id);
                      }}
                    >
                      {t('seasons.edit')}
                    </button>
                  </SheetRow>
                  {editing === s.id ? (
                    <EditSeason season={s} onDone={() => setEditing(null)} />
                  ) : null}
                  {expanded === s.id ? <Phases seasonId={s.id} locale={locale} /> : null}
                </li>
              );
            })}
          </Sheet>
        )}
      </Panel>
    </>
  );
}

/**
 * Editing the season itself.
 *
 * A season could be created and never changed, which sounds harmless until the
 * first session turns out to predate the start date somebody typed in July. The
 * dates are a guess until the fixture list arrives; the team is not editable,
 * because moving a season to another squad would quietly reattach a year of work.
 */
function EditSeason({
  season,
  onDone,
}: {
  season: { id: string; label: string; startsOn: string; endsOn: string; status: string };
  onDone: () => void;
}) {
  const t = useTranslations();
  const update = useUpdateSeason(season.id);
  const [form, setForm] = useState<SeasonEditInput>({
    label: season.label,
    startsOn: season.startsOn,
    endsOn: season.endsOn,
    status: season.status as SeasonEditInput['status'],
  });

  const invalid = form.endsOn < form.startsOn;

  return (
    <div className="border-t border-line bg-surface-sunken px-4 py-4">
      <h3 className="mb-3 text-meta font-semibold uppercase tracking-wide text-ink-muted">
        {t('seasons.edit')}
      </h3>
      <div className="grid gap-x-5 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t('seasons.label')}>
          {(p) => (
            <TextInput
              {...p}
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            />
          )}
        </Field>
        <Field label={t('seasons.startsOn')}>
          {(p) => (
            <TextInput
              {...p}
              type="date"
              value={form.startsOn}
              onChange={(e) => setForm((f) => ({ ...f, startsOn: e.target.value }))}
            />
          )}
        </Field>
        <Field label={t('seasons.endsOn')} error={invalid ? t('seasons.endsBeforeStarts') : undefined}>
          {(p) => (
            <TextInput
              {...p}
              type="date"
              value={form.endsOn}
              onChange={(e) => setForm((f) => ({ ...f, endsOn: e.target.value }))}
            />
          )}
        </Field>
        <Field label={t('seasons.status')}>
          {(p) => (
            <SelectInput
              {...p}
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value as SeasonEditInput['status'] }))
              }
            >
              <option value="planning">{t('seasons.status_planning')}</option>
              <option value="active">{t('seasons.status_active')}</option>
              <option value="archived">{t('seasons.status_archived')}</option>
            </SelectInput>
          )}
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          busy={update.isPending}
          disabled={invalid || !form.label.trim()}
          onClick={async () => {
            await update.mutateAsync(form);
            onDone();
          }}
        >
          {t('seasons.save')}
        </Button>
        <Button variant="quiet" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        {update.isError ? <Notice tone="risk">{t('common.saveFailed')}</Notice> : null}
      </div>
    </div>
  );
}

/**
 * The blocks a coach actually works in. The objective field is the first link in
 * the chain of reasons — everything planned inside a phase can point back to it.
 */
function Phases({ seasonId, locale }: { seasonId: string; locale: Locale }) {
  const t = useTranslations();
  const phases = usePhases(seasonId);
  const create = useCreatePhase(seasonId);
  const update = useUpdatePhase(seasonId);
  const remove = useDeletePhase(seasonId);
  const { busy, formError, fieldError, submit } = useAuthForm();
  const [open, setOpen] = useState(false);
  // The same form writes a new phase and edits an existing one: a phase has
  // five fields, and a second form for the same five would drift.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<SeasonPhaseInput>({
    name: '',
    kind: undefined,
    startsOn: '',
    endsOn: '',
    objectiveSummary: undefined,
    sort: 0,
  });

  const set = <K extends keyof SeasonPhaseInput>(k: K) => (value: SeasonPhaseInput[K]) =>
    setForm((f) => ({ ...f, [k]: value }));

  const list = phases.data ?? [];

  const blank = (): SeasonPhaseInput => ({
    name: '',
    kind: undefined,
    startsOn: '',
    endsOn: '',
    objectiveSummary: undefined,
    sort: 0,
  });

  const startCreate = () => {
    setEditingId(null);
    setForm(blank());
    setOpen(true);
  };

  const startEdit = (phase: (typeof list)[number]) => {
    setEditingId(phase.id);
    setForm({
      name: phase.name,
      kind: phase.kind ?? undefined,
      startsOn: phase.startsOn,
      endsOn: phase.endsOn,
      objectiveSummary: phase.objectiveSummary ?? undefined,
      sort: phase.sort,
    });
    setOpen(true);
  };

  return (
    <div className="border-t border-line bg-surface-sunken px-4 py-4">
      <div className="mb-3 flex items-center gap-3">
        <h3 className="font-condensed font-semibold">{t('seasons.phases')}</h3>
        <span className="tabular text-meta text-ink-faint">{list.length}</span>
        <Button
          variant="secondary"
          className="ms-auto !min-h-9 !text-meta"
          onClick={() => (open ? setOpen(false) : startCreate())}
        >
          {t('seasons.addPhase')}
        </Button>
      </div>

      {open ? (
        <form
          noValidate
          className="mb-4 grid gap-x-4 gap-y-2 rounded-(--radius-control) border border-line bg-surface p-3 sm:grid-cols-2"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await submit(async () => {
              if (editingId) {
                await update.mutateAsync({ phaseId: editingId, input: form });
              } else {
                await create.mutateAsync({ ...form, sort: list.length * 10 });
              }
            });
            if (ok) {
              setOpen(false);
              setEditingId(null);
              setForm(blank());
            }
          }}
        >
          <h4 className="font-condensed text-meta font-semibold uppercase tracking-wide text-ink-faint sm:col-span-2">
            {t(editingId ? 'seasons.editPhase' : 'seasons.addPhase')}
          </h4>

          {formError ? (
            <div className="sm:col-span-2">
              <Notice tone="risk">{formError}</Notice>
            </div>
          ) : null}

          <Field label={t('seasons.phaseName')} error={fieldError('name')}>
            {(p) => (
              <TextInput
                {...p}
                value={form.name}
                onChange={(e) => set('name')(e.target.value)}
                required
                autoFocus
              />
            )}
          </Field>

          <Field label={t('seasons.phaseKind')} optional error={fieldError('kind')}>
            {(p) => (
              <TextInput
                {...p}
                value={form.kind ?? ''}
                onChange={(e) => set('kind')(e.target.value || undefined)}
              />
            )}
          </Field>

          <Field label={t('seasons.startsOn')} error={fieldError('startsOn')}>
            {(p) => (
              <TextInput
                {...p}
                type="date"
                value={form.startsOn}
                onChange={(e) => set('startsOn')(e.target.value)}
                required
              />
            )}
          </Field>

          <Field label={t('seasons.endsOn')} error={fieldError('endsOn')}>
            {(p) => (
              <TextInput
                {...p}
                type="date"
                value={form.endsOn}
                onChange={(e) => set('endsOn')(e.target.value)}
                required
              />
            )}
          </Field>

          <div className="sm:col-span-2">
            <Field
              label={t('seasons.phaseObjective')}
              hint={t('seasons.phaseObjectiveHint')}
              optional
              error={fieldError('objectiveSummary')}
            >
              {(p) => (
                <TextArea
                  {...p}
                  value={form.objectiveSummary ?? ''}
                  onChange={(e) => set('objectiveSummary')(e.target.value || undefined)}
                />
              )}
            </Field>
          </div>

          <div className="flex gap-2 sm:col-span-2">
            <Button type="submit" variant="primary" busy={busy} className="!min-h-9 !text-meta">
              {t('seasons.savePhase')}
            </Button>
            <Button
              type="button"
              variant="quiet"
              className="!min-h-9 !text-meta"
              onClick={() => {
                setOpen(false);
                setEditingId(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            {editingId ? (
              <Button
                type="button"
                variant="danger"
                className="ms-auto !min-h-9 !text-meta"
                onClick={() => {
                  if (!window.confirm(t('seasons.confirmDeletePhase'))) return;
                  void remove.mutateAsync(editingId).then(() => {
                    setOpen(false);
                    setEditingId(null);
                  });
                }}
              >
                {t('seasons.deletePhase')}
              </Button>
            ) : null}
          </div>
        </form>
      ) : null}

      {phases.isLoading ? (
        <p className="flex items-center gap-2 text-meta text-ink-muted">
          <Spinner /> {t('common.loading')}
        </p>
      ) : list.length === 0 ? (
        <div className="rounded-(--radius-control) border border-dashed border-line px-4 py-6 text-center">
          <p className="font-medium">{t('seasons.noPhases')}</p>
          <p className="mx-auto mt-1 max-w-[44ch] text-meta text-ink-muted">
            {t('seasons.noPhasesBody')}
          </p>
        </div>
      ) : (
        <ol className="divide-y divide-line overflow-hidden rounded-(--radius-control) border border-line bg-surface">
          {list.map((phase, index) => (
            <li key={phase.id}>
              <button
                type="button"
                onClick={() => startEdit(phase)}
                className={`flex w-full items-start gap-3 px-3 py-2.5 text-start transition-colors hover:bg-surface-sunken ${
                  editingId === phase.id ? 'bg-signal-soft' : ''
                }`}
              >
              {/* Numbered because a season's phases genuinely are a sequence. */}
              <SheetNumber>{index + 1}</SheetNumber>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium">{phase.name}</span>
                  {phase.kind ? <Chip muted>{phase.kind}</Chip> : null}
                </span>
                <span className="tabular block text-meta text-ink-faint">
                  {formatDateRange(phase.startsOn, phase.endsOn, locale)}
                </span>
                {phase.objectiveSummary ? (
                  <span className="mt-1 block max-w-[70ch] text-meta text-ink-muted">
                    {phase.objectiveSummary}
                  </span>
                ) : null}
              </span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
